// ============================================================
// WorkspaceModuleCard — a software capability, not a plain form.
//
// The module-card grammar from the QUAM command center: glyph
// tile with accent glow, title + status chip, summary, optional
// mini-stats row, optional actions. Used by Work Collaboration,
// Google Vault, Damages, Agent Control, and the cockpit home.
//
// Honesty contract: `status` should reflect the module's REAL
// state (powered / requires setup / approval required / coming
// later) — never render a fake ready state.
// ============================================================

import { useState } from 'react'
import WorkspaceStatusChip from './WorkspaceStatusChip'
import {
  tint,
  WORKSPACE_ACCENTS,
  WORKSPACE_FONT,
  WORKSPACE_SURFACE,
  WORKSPACE_TEXT,
} from './workspaceTheme'

export default function WorkspaceModuleCard({
  glyph,
  title,
  status,
  statusLabel,
  accent = WORKSPACE_ACCENTS.cyan,
  summary,
  meta,              // [{ label, value, accent? }] — mini stats row
  footnote,
  actions,           // ReactNode — rendered bottom-right
  onOpen,
  children,
  style: styleOverride,
}) {
  const [hover, setHover] = useState(false)
  const clickable = typeof onOpen === 'function'

  return (
    <article
      onClick={clickable ? onOpen : undefined}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen() } } : undefined}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '16px 16px 14px',
        borderRadius: 'var(--sc-radius-lg, 12px)',
        background: hover && clickable ? tint(accent, 6) : WORKSPACE_SURFACE.card,
        border: `1px solid ${hover && clickable ? tint(accent, 26) : WORKSPACE_SURFACE.line}`,
        boxShadow: hover && clickable ? `0 0 18px ${tint(accent, 10)}` : 'none',
        transition: 'all 0.25s ease',
        cursor: clickable ? 'pointer' : 'default',
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {glyph && (
          <div
            aria-hidden
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              color: accent,
              background: tint(accent, 12),
              border: `1px solid ${tint(accent, 22)}`,
              boxShadow: `0 0 14px ${tint(accent, 12)}`,
            }}
          >
            {glyph}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: WORKSPACE_TEXT.primary,
                fontFamily: WORKSPACE_FONT.ui,
              }}
            >
              {title}
            </span>
            {status != null && <WorkspaceStatusChip status={status} label={statusLabel} size="sm" />}
          </div>
          {summary && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12.5,
                lineHeight: 1.65,
                color: WORKSPACE_TEXT.tertiary,
                fontFamily: WORKSPACE_FONT.ui,
              }}
            >
              {summary}
            </div>
          )}
        </div>
      </div>

      {meta && meta.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(meta.length, 4)}, 1fr)`,
            gap: 6,
          }}
        >
          {meta.map((m, i) => (
            <div
              key={`${m.label}-${i}`}
              style={{
                padding: '8px 6px',
                borderRadius: 8,
                textAlign: 'center',
                background: tint(m.accent || accent, 6),
                border: `1px solid ${tint(m.accent || accent, 12)}`,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: m.accent || accent,
                  fontFamily: WORKSPACE_FONT.mono,
                  fontVariantNumeric: 'tabular-nums',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {m.value}
              </div>
              <div
                style={{
                  marginTop: 2,
                  fontSize: 8,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: WORKSPACE_TEXT.muted,
                  fontFamily: WORKSPACE_FONT.mono,
                }}
              >
                {m.label}
              </div>
            </div>
          ))}
        </div>
      )}

      {children}

      {(footnote || actions) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginTop: 'auto',
          }}
        >
          <span
            style={{
              fontSize: 10,
              color: WORKSPACE_TEXT.muted,
              fontFamily: WORKSPACE_FONT.mono,
              letterSpacing: '0.04em',
            }}
          >
            {footnote}
          </span>
          <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        </div>
      )}
    </article>
  )
}
